import React, { useState, useEffect, useRef } from 'react'
import { 
  Play, 
  Pause, 
  Settings as SettingsIcon,
  Download,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingDown,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { automationAPI, kiteAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import toast from 'react-hot-toast'

const AutomationPanel = ({ holdings = [] }) => {
  const [settings, setSettings] = useState({
    isActive: true,
    enableNegativeExitOrders: false,
    enablePositiveProfitOrders: false,
    negativeExitThreshold: 1.0,
    positiveTriggerThreshold: 1.0,
    positivePlaceAbove: 0.1,
    priceRoundingUnit: 0.5,
    pollInterval: 60,
    dryRun: false,
    minQuantityToSell: 0.5,
    maxOrdersPerPoll: 10,
    saveLogsToMongoDB: true
  })
  const [logs, setLogs] = useState([])
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [logFilter, setLogFilter] = useState('all')
  const [expandedLogs, setExpandedLogs] = useState(new Set())
  const [showSettings, setShowSettings] = useState(false)
  const [lastRunResult, setLastRunResult] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('idle') // idle, running, error
  
  const intervalRef = useRef(null)
  const isRunningRef = useRef(false)

  useEffect(() => {
    fetchSettings()
    fetchLogs()
    fetchOrders()
  }, [])

  useEffect(() => {
    // Don't auto-start automation - user must manually start
    if (!settings.isActive && isRunningRef.current) {
      stopAutomation()
    }
  }, [settings.isActive])

  const fetchSettings = async () => {
    try {
      const response = await automationAPI.getSettings()
      setSettings(response.data)
    } catch (error) {
      console.error('Failed to fetch automation settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchLogs = async () => {
    try {
      const params = logFilter !== 'all' ? { level: logFilter } : {}
      const response = await automationAPI.getLogs(params)
      setLogs(response.data.logs || [])
    } catch (error) {
      console.error('Failed to fetch logs:', error)
    }
  }

  const fetchOrders = async () => {
    try {
      const response = await automationAPI.getOrders()
      setOrders(response.data || [])
    } catch (error) {
      console.error('Failed to fetch orders:', error)
    }
  }

  const updateSettings = async (newSettings) => {
    try {
      const response = await automationAPI.updateSettings(newSettings)
      setSettings(response.data)
      toast.success('Settings updated')
    } catch (error) {
      console.error('Failed to update settings:', error)
      toast.error('Failed to update settings')
    }
  }

  const startAutomation = () => {
    if (isRunningRef.current) return
    
    isRunningRef.current = true
    setRunning(true)
    
    // Run immediately
    runCycle()
    
    // Set up interval
    intervalRef.current = setInterval(() => {
      runCycle()
    }, settings.pollInterval * 1000)
    
    toast.success('CNC Automation started')
  }

  const stopAutomation = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    
    isRunningRef.current = false
    setRunning(false)
    toast.info('CNC Automation stopped')
  }

  const runCycle = async () => {
    try {
      await automationAPI.runCycle()
      fetchLogs()
      fetchOrders()
    } catch (error) {
      console.error('Automation cycle failed:', error)
      toast.error('Automation cycle failed')
    }
  }

  const toggleAutomation = () => {
    const newIsActive = !settings.isActive
    if (newIsActive) {
      // Check if at least one order type is enabled before starting
      if (!settings.enableNegativeExitOrders && !settings.enablePositiveProfitOrders) {
        toast.error('Please enable at least one order type (Loss Exit or Profit Exit) before starting automation')
        return
      }
      // Start automation manually
      updateSettings({ ...settings, isActive: newIsActive })
      startAutomation()
    } else {
      // Stop automation
      updateSettings({ ...settings, isActive: newIsActive })
      stopAutomation()
    }
  }

  const clearLogs = async () => {
    try {
      await automationAPI.clearLogs()
      setLogs([])
      toast.success('Logs cleared')
    } catch (error) {
      console.error('Failed to clear logs:', error)
      toast.error('Failed to clear logs')
    }
  }

  const exportLogs = async () => {
    try {
      const response = await automationAPI.exportLogs({ format: 'csv' })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'automation-logs.csv'
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success('Logs exported')
    } catch (error) {
      console.error('Failed to export logs:', error)
      toast.error('Failed to export logs')
    }
  }

  const forceSell = async (holding) => {
    try {
      if (!holding || !holding.tradingsymbol) {
        toast.error('Invalid holding data');
        return;
      }
      
      // Fetch fresh LTP
      let ltp = holding.last_price || 0;
      
      try {
        const cleanSymbol = holding.tradingsymbol.replace('-EQ', '');
        const quoteResponse = await kiteAPI.getQuote(cleanSymbol);
        ltp = quoteResponse.data?.data?.last_price || holding.last_price || 0;
      } catch (quoteError) {
        console.warn('Failed to fetch fresh LTP, using holding LTP:', quoteError.message);
        // Continue with holding LTP
      }
      
      if (!ltp) {
        toast.error('Unable to fetch current LTP')
        return
      }

      const totalQuantity = (holding.quantity || 0) + (holding.t1_quantity || 0);
      const quantity = Math.floor(totalQuantity * (settings.minQuantityToSell || 0.5));
      const price = roundToUnit(ltp, settings.priceRoundingUnit || 0.5);
      
      if (quantity <= 0) {
        toast.error(`Invalid quantity calculated: ${quantity}`);
        return;
      }

      const confirmed = window.confirm(
        `Force sell ${quantity} shares of ${holding.tradingsymbol} at ₹${price}?`
      )

      if (!confirmed) return

      await automationAPI.forceSell(holding.tradingsymbol, { quantity, price });
      toast.success(`Force sell order placed for ${holding.tradingsymbol}`)
      fetchOrders()
      fetchLogs()
    } catch (error) {
      console.error('Force sell failed:', error)
      toast.error(`Force sell failed: ${error.response?.data?.message || error.message}`)
    }
  }

  const toggleLogExpansion = (logId) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev)
      if (newSet.has(logId)) {
        newSet.delete(logId)
      } else {
        newSet.add(logId)
      }
      return newSet
    })
  }

  const filteredLogs = logs.filter(log => 
    logFilter === 'all' || log.level === logFilter
  )

  const getLogIcon = (level) => {
    switch (level) {
      case 'error': return <AlertCircle className="w-4 h-4 text-danger-600" />
      case 'warn': return <AlertCircle className="w-4 h-4 text-yellow-600" />
      default: return <CheckCircle className="w-4 h-4 text-blue-600" />
    }
  }

  const roundToUnit = (price, unit = 0.5) => {
    return Math.round(price / unit) * unit
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Automation Controls */}
      <div className="card border-2 border-primary-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <TrendingDown className="w-6 h-6 text-primary-600" />
            <div>
              <h3 className="text-lg font-semibold text-primary-800">Automated CNC Sell</h3>
              <p className="text-sm text-primary-600">Auto-exit positions based on P&L thresholds</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`flex items-center space-x-2 text-sm ${
              running ? 'text-success-600' : 'text-gray-600'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                running ? 'bg-success-600 animate-pulse' : 'bg-gray-400'
              }`} />
              <span>{running ? 'Running' : 'Stopped'}</span>
            </div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="btn-secondary flex items-center space-x-2"
            >
              <SettingsIcon className="w-4 h-4" />
              <span>Settings</span>
            </button>
            <button
              onClick={toggleAutomation}
              className={`btn flex items-center space-x-2 ${
                settings.isActive ? 'btn-danger' : 'btn-success'
              }`}
            >
              {settings.isActive ? (
                <>
                  <Pause className="w-4 h-4" />
                  <span>Pause</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Start</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="border-t border-gray-200 pt-4 space-y-4">
            {/* Order Type Controls */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-semibold text-blue-900 mb-3">Order Controls</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Exit Loss Orders</p>
                    <p className="text-sm text-gray-600">Place sell orders when holdings are below loss threshold</p>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      enableNegativeExitOrders: !prev.enableNegativeExitOrders
                    }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.enableNegativeExitOrders ? 'bg-danger-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.enableNegativeExitOrders ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Profit Exit Orders</p>
                    <p className="text-sm text-gray-600">Place sell orders when holdings exceed profit threshold</p>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      enablePositiveProfitOrders: !prev.enablePositiveProfitOrders
                    }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.enablePositiveProfitOrders ? 'bg-success-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.enablePositiveProfitOrders ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              {!settings.enableNegativeExitOrders && !settings.enablePositiveProfitOrders && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Both order types are disabled. Enable at least one to place automated orders.
                  </p>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Negative Exit Threshold (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10"
                  value={settings.negativeExitThreshold}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    negativeExitThreshold: parseFloat(e.target.value) 
                  }))}
                  className="input text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Positive Trigger Threshold (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="10"
                  value={settings.positiveTriggerThreshold}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    positiveTriggerThreshold: parseFloat(e.target.value) 
                  }))}
                  className="input text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Positive Place Above (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="2"
                  value={settings.positivePlaceAbove}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    positivePlaceAbove: parseFloat(e.target.value) 
                  }))}
                  className="input text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Rounding Unit
                </label>
                <select
                  value={settings.priceRoundingUnit}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    priceRoundingUnit: parseFloat(e.target.value) 
                  }))}
                  className="input text-sm"
                >
                  <option value={0.05}>₹0.05</option>
                  <option value={0.1}>₹0.10</option>
                  <option value={0.5}>₹0.50</option>
                  <option value={1.0}>₹1.00</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Poll Interval (sec)
                </label>
                <input
                  type="number"
                  min="30"
                  max="300"
                  value={settings.pollInterval}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    pollInterval: parseInt(e.target.value) 
                  }))}
                  className="input text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Quantity to Sell
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="1"
                  value={settings.minQuantityToSell}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    minQuantityToSell: parseFloat(e.target.value) 
                  }))}
                  className="input text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Orders Per Poll
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={settings.maxOrdersPerPoll}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    maxOrdersPerPoll: parseInt(e.target.value) 
                  }))}
                  className="input text-sm"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="text-sm">
                  <p className="font-medium text-gray-700 mb-1">Order Status:</p>
                  <div className="flex items-center space-x-4">
                    <span className={`text-xs px-2 py-1 rounded ${
                      settings.enableNegativeExitOrders ? 'bg-danger-100 text-danger-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      Loss Exit: {settings.enableNegativeExitOrders ? 'ON' : 'OFF'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      settings.enablePositiveProfitOrders ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      Profit Exit: {settings.enablePositiveProfitOrders ? 'ON' : 'OFF'}
                    </span>
                  </div>
                </div>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.dryRun}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      dryRun: e.target.checked 
                    }))}
                  />
                  <span className="text-sm font-medium text-gray-700">Dry Run Mode</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.saveLogsToMongoDB}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      saveLogsToMongoDB: e.target.checked 
                    }))}
                  />
                  <span className="text-sm font-medium text-gray-700">Save Logs to MongoDB</span>
                </label>
              </div>
              
              <button
                onClick={() => updateSettings(settings)}
                className="btn-primary flex items-center space-x-2"
              >
                <SettingsIcon className="w-4 h-4" />
                <span>Save Settings</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Holdings with Force Sell */}
      {holdings.length > 0 && (
        <div className="card">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Holdings - Force Sell</h4>
          <div className="space-y-2">
            {holdings.slice(0, 5).map((holding) => {
              const quantity = (holding.quantity || 0) + (holding.t1_quantity || 0)
              const pnlPercent = holding.average_price > 0 ? 
                ((holding.pnl || 0) / (holding.average_price * quantity)) * 100 : 0
              
              return (
                <div key={holding.tradingsymbol} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{holding.tradingsymbol}</span>
                    <div className="text-sm text-gray-600">
                      {quantity} @ ₹{holding.average_price?.toFixed(2)} 
                      <span className={`ml-2 ${pnlPercent >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                        ({pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => forceSell(holding)}
                    className="btn-danger text-xs px-3 py-1"
                  >
                    Force Sell
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent Orders */}
      {orders.length > 0 && (
        <div className="card">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Automation Orders</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {orders.slice(0, 10).map((order) => (
              <div key={order._id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div>
                  <span className="font-medium">{order.symbol}</span>
                  <div className="text-sm text-gray-600">
                    {order.quantity} @ ₹{order.price.toFixed(2)}
                    {order.isDryRun && <span className="ml-2 text-blue-600">(DRY RUN)</span>}
                  </div>
                </div>
                <span className={`badge text-xs ${
                  order.status === 'placed' ? 'badge-success' :
                  order.status === 'failed' ? 'badge-danger' : 'badge-info'
                }`}>
                  {order.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs Panel */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Automation Logs</h4>
          <div className="flex items-center space-x-3">
            <select
              value={logFilter}
              onChange={(e) => {
                setLogFilter(e.target.value)
                fetchLogs()
              }}
              className="input text-sm"
            >
              <option value="all">All Logs</option>
              <option value="info">Info</option>
              <option value="warn">Warnings</option>
              <option value="error">Errors</option>
            </select>
            <button
              onClick={exportLogs}
              className="btn-secondary flex items-center space-x-2 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button
              onClick={clearLogs}
              className="btn-danger flex items-center space-x-2 text-sm"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
        
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredLogs.map((log) => (
            <div key={log._id} className="border border-gray-200 rounded-lg">
              <div 
                className="flex items-start space-x-3 p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleLogExpansion(log._id)}
              >
                <div className="flex-shrink-0 mt-1">
                  {getLogIcon(log.level)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{log.message}</p>
                    <div className="flex items-center space-x-2">
                      {log.symbol && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {log.symbol}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {new Date(log.createdAt).toLocaleTimeString()}
                      </span>
                      {expandedLogs.has(log._id) ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {expandedLogs.has(log._id) && (
                <div className="border-t border-gray-200 p-3 bg-gray-50">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(log.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
          
          {filteredLogs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No logs available</p>
              <p className="text-sm">Automation logs will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AutomationPanel